import ReactEcharts from 'echarts-for-react'
import { useState } from 'react'

import {
  BasicSelectionBox,
  BasicTable,
  ContentCard,
} from '@/components/customComponents'

import { lineChartOptions } from '@/constants/CHART_CONSTANTS'
import { useAllSkus, usePriceHistory } from '@/hooks'
import { useReduxBaseFilter } from '@/store'
import { PriceHistoryMonthlyModel } from '@/types/clarityLens'
import { dateToString } from '@/utils'

interface PriceHistoryTabProps {
  itemName: string
}

export const PriceHistoryTab = ({
  itemName: itemName,
}: PriceHistoryTabProps) => {
  // Get filter state from Redux
  const { selectedBranch, date } = useReduxBaseFilter()
  const [averageRccPriceFilter, setAverageRccPriceFilter] =
    useState<string>('ptiMoneyImpact')

  const { seasonalityChartData, loading: priceHistoryLoading } =
    usePriceHistory(
      dateToString(date, 'from'),
      dateToString(date, 'to'),
      selectedBranch,
      itemName,
      averageRccPriceFilter
    )

  const { allSkusData, allSkusColumns, loading } = useAllSkus(
    dateToString(date, 'from'),
    dateToString(date, 'to'),
    selectedBranch,
    itemName
  )

  return (
    <div className="space-y-6">
      <div>
        <ContentCard
          title="Average Material Code Price"
          loading={priceHistoryLoading}
          customButton={
            <BasicSelectionBox
              options={[
                { value: 'ptiMoneyImpact', label: 'PTI $ Impact' },
                { value: 'ptiPercentage', label: 'PTI %' },
                { value: 'ptiVsCti', label: 'PTI % vs CTI %' },
              ]}
              onValueChange={setAverageRccPriceFilter}
              defaultValue={averageRccPriceFilter || 'ptiMoneyImpact'}
              className="w-[160px]"
            />
          }
        >
          {seasonalityChartData && (
            <ReactEcharts
              option={getLineChartData(
                averageRccPriceFilter,
                seasonalityChartData?.data
              )}
            />
          )}
        </ContentCard>
      </div>
      <div>
        <BasicTable
          title="All SKUs"
          data={allSkusData}
          columns={allSkusColumns}
          loading={loading}
          enablePagination
        />
      </div>
    </div>
  )
}

const getLineChartData = (metric: string, data?: PriceHistoryMonthlyModel) => {
  const metricType = metric === 'ptiMoneyImpact' ? 'money' : 'percentage'

  // Safety check for empty data
  if (!data || Object.keys(data).length === 0) {
    return lineChartOptions([], [], [], metricType)
  }

  // Extract month names and values from PriceHistoryMonthlyModel
  const monthNames = Object.keys(data) as Array<keyof PriceHistoryMonthlyModel>
  const values = monthNames.map((month) => data[month])

  // Safety check for no months found
  if (monthNames.length === 0) {
    return lineChartOptions([], [], [], metricType)
  }

  // Create raw data as a single series
  const rawData = [values]
  const seriesNames = ['Price History']

  return lineChartOptions(rawData, seriesNames, monthNames, metricType)
}
